import React, { useEffect, useState, useRef } from "react";
import { Box, Spinner, Heading, useTheme } from "@chakra-ui/react";
import { Bar } from "react-chartjs-2";
import axios from "axios";
import ChartJS from "../../../tools/ChartSetup";
import gradient from "chartjs-plugin-gradient";

const Chart5Region = ({ supplier, retailer, region }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
        const fetchData = async () => {
          setLoading(true);
          try {
            const res = await axios.post(
              `${process.env.REACT_APP_API_URL}/rep_sales/chart5_chk_region`,
              { supplier, retailer, region, userid: localStorage.getItem("userid") },
              { signal: controller.signal }
            );
            const data = res.data.data;
            const ctx = chartRef.current?.ctx;
            setChartData({
              labels: data.map(i =>
                new Date(i.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })
              ),
              datasets: [
                {
                  label: "Units",
                  data: data.map(i => i.value),
                  backgroundColor: data.map(() => {
                    if (ctx) {
                      const g = ctx.createLinearGradient(0, 0, 0, 400);
                      g.addColorStop(0, "rgba(88, 192, 135, 0.8)");
                      g.addColorStop(1, "rgba(88, 192, 135, 0.3)");
                      return g;
                    }
                    return "rgba(88, 192, 135, 0.8)";
                  }),
                  borderColor: "transparent",
                  borderWidth: 0,
                  borderRadius: { topLeft: 4, topRight: 4 },
                },
              ],
            });
          } catch (err) {
            if (!axios.isCancel(err)) console.error("chart5 region fetch failed", err);
          } finally {
            if (!controller.signal.aborted) setLoading(false);
          }
        };
        fetchData();
        return () => {
          controller.abort();
          if (chartRef.current?.chartInstance) chartRef.current.chartInstance.destroy();
        };
     }, [supplier, retailer, region]);

  if (loading) return <Spinner />;

  return (
    <Box height="100%">
      <Heading size="xs" color="gray.600" mb={2} fontWeight="600">
        Monthly Sales - Units ({region})
      </Heading>
      {chartData && (
        <Bar
          ref={chartRef}
          data={chartData}
          options={{
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              datalabels: {
                display: true,
                align: "top",
                anchor: "end",
                color: "#6b7280",
                backgroundColor: "transparent",
                borderRadius: 0,
                padding: 2,
                font: { size: 9 },
                formatter: function (value) {
                  if (typeof value === "number") {
                    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value;
                  }
                  return value;
                },
              },
              tooltip: {
                backgroundColor: "rgba(255,255,255,0.95)",
                titleColor: "#374151",
                bodyColor: "#6b7280",
                borderColor: "#e5e7eb",
                borderWidth: 1,
                cornerRadius: 6,
                titleFont: { size: 12, weight: "600" },
                bodyFont: { size: 12 },
                padding: 10,
                callbacks: {
                  label: function (ctx) {
                    const v = ctx.raw;
                    if (v >= 1000000) return ` ${(v / 1000000).toFixed(1)}M`;
                    if (v >= 1000) return ` ${(v / 1000).toFixed(0)}K`;
                    return ` ${v.toLocaleString()}`;
                  },
                },
              },
              filler: { propagate: false },
            },
            scales: {
              x: {
                ticks: { color: "#9ca3af", font: { size: 11 } },
                grid: { display: false },
              },
              y: {
                ticks: { color: "#9ca3af", font: { size: 11 } },
                grid: {
                  display: true,
                  color: "rgba(229,231,235,0.5)",
                  drawBorder: false,
                  borderDash: [3, 3],
                },
              },
            },
          }}
        />
      )}
    </Box>
  );
};

export default Chart5Region;
